import { z } from 'zod'
import { isValidEthereumAddress } from '@/server/lib/ethereum'
import { isValidBitcoinAddress } from '@/server/lib/bitcoin'

export const SignInEmailPasswordSchema =
  z.object({
    email: z.string().trim().email(),
    password: z.string().min(1),
  })

const passwordSchema = z.string().min(8).max(128)

export const titleOptions = [
  "mr",
  "mrs",
  "ms",
  "other"
] as const

export const PersonalDetailsSchema = z.object({
  title: z.enum(titleOptions),
  firstName: z.string().trim().min(1).max(50),
  lastName: z.string().trim().min(1).max(50),
  dateOfBirth: z.string()
    .refine((date) => /^\d{4}-\d{2}-\d{2}([T ][\d:.+-Z]*)?$/.test(date))
    .refine((date) => {
      const birthDate = new Date(date)
      if (isNaN(birthDate.getTime())) return false
      const today = new Date()
      let age = today.getFullYear() - birthDate.getFullYear()
      const monthDiff = today.getMonth() - birthDate.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--
      }
      return age >= 18 && age <= 120
    }, {
      message: "Age must be between 18 and 120",
    }),
  countryOfBirth: z.string().trim().min(1),
  citizenship: z.array(z.string().trim().min(1)).min(1),
})

export const ContactDetailsSchema = z.object({
  country: z.string().trim().min(1),
  city: z.string().trim().min(1).max(100),
  address: z.string().trim().min(1).max(200),
  postalCode: z.string().trim().min(1).max(20),
  phoneNumber: z.string().trim().min(1).regex(/^[\+][(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/),
})

export const AccountDetailsSchema = z.object({
  email: z.string().trim().email(),
  password: passwordSchema,
  inviteCode: z.string().trim().min(1),
})

export const signUpPasswordMatchValidation = (val: { password: string, email: string, firstName: string, lastName: string }, ctx: z.RefinementCtx) => {
  if (
    val.password.toLowerCase() === val.email.toLowerCase() ||
    val.password.toLowerCase() === val.firstName.toLowerCase() ||
    val.password.toLowerCase() === val.lastName.toLowerCase() ||
    val.password.toLowerCase() === (val.firstName + val.lastName).toLowerCase()
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["password"],
      params: {
        i18n: "errors.password_same_as_name_or_email",
      }
    })
  }
}

export const SignUpSchema = PersonalDetailsSchema
  .extend(ContactDetailsSchema.shape)
  .extend(AccountDetailsSchema.shape)
  .superRefine(signUpPasswordMatchValidation)

export const UpdateProfileSchema = PersonalDetailsSchema
  .extend(ContactDetailsSchema.shape)
  .extend(AccountDetailsSchema.shape)
  .omit({ password: true, inviteCode: true })

export const ForgotPasswordSchema =
  z.object({
    email: z.string().trim().email()
  })

export const ResetPasswordSchema =
  z.object({
    newPassword: passwordSchema,
    token: z.string().trim().min(1),
  })

export const ChangePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(8),
  confirmPassword: z.string().min(1)
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
})

export const VerifyOtpSchema =
  z.object({
    code: z.string().trim().min(6).max(6)
  })